// src/pages/onboarding/OnboardingRouter.jsx
import React from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import FirstUseCookieGate from "@/components/consent/FirstUseCookieGate.jsx";
import { useAuth } from "@/contexts/AuthContext";
import { clearLastRole, readLastRole, writeLastRole } from "@/lib/roleCache";
import { readLastAuthedPath, writeLastAuthedPath } from "@/lib/authedPathCache";
import { loadProgress } from "@/lib/persistStep";
import { shouldShowSettingsWelcome } from "@/lib/settingsWelcomeState.js";
import RouteFallback from "@/sot/primitives/RouteFallback.jsx";
import { writeOnboardingEvent, writeUserActivityEvent } from "@/services/userTruthTracking.js";
import { logAuthAttempt } from "@/services/authAttemptService.js";
import { normalizeCanonicalRole } from "@/lib/roleResolution.mjs";
import { hasPasswordConfigured } from "@/features/security/accountSecurityState.js";
import { getPrivacySafeTelemetryRoute } from "@/lib/onboardingNavigationTelemetry.js";
import {
  advanceStagingFirstUse,
  moveResearchFirstUseBack,
  resolveStagingFirstUseDisposition,
  selectSelfServiceFirstUseCategory,
} from "@/services/stagingAccessService.js";

import StepCategory        from "@/pages/onboarding/shared/steps/StepCategory.jsx";
import DoctorOnboarding    from "@/pages/onboarding/doctor/DoctorOnboarding.jsx";
import ControlledDoctorFirstUse from "@/pages/onboarding/doctor/ControlledDoctorFirstUse.jsx";
import PatientOnboarding   from "@/pages/onboarding/patient/PatientOnboarding.jsx";
import ChangePassword from "@/pages/profile-settings/patient/ChangePassword.jsx";
import EmployerOnboarding  from "@/pages/onboarding/employer/EmployerOnboarding.jsx";
import MoHOnboarding       from "@/pages/onboarding/moh/MoHOnboarding.jsx";
import PharmacyOnboarding  from "@/pages/onboarding/pharmacy/PharmacyOnboarding.jsx";
import ResearchPublicHealthOnboarding from "@/pages/onboarding/research/ResearchPublicHealthOnboarding.jsx";
import OnboardingSection from "@/components/onboarding/OnboardingSection.jsx";
import OnboardingTopBar from "@/components/onboarding/OnboardingTopBar.jsx";
import Button from "@/components/ui/Button.jsx";

const CATS = new Set(["doctor", "patient", "research", "employer", "moh", "pharmacy"]);
const CATEGORY_EVENT_KEY = "step_category";
const PATIENT_PROGRESS_KEY = "onb_patient_v2";
const DOCTOR_PROGRESS_KEY = "onb_doctor_v2";
const PICKABLE_CATS = new Set(["doctor", "patient", "pharmacy"]);

function PatientFirstUseStart({ onContinue, onBack }) {
  return (
    <div className="min-h-dvh flex flex-col">
      <OnboardingTopBar step={1} total={1} onBack={onBack} />
      <OnboardingSection
        header={
          <div className="space-y-2">
            <h1 className="text-title-large text-on-surface">Get started</h1>
            <p className="text-body-medium text-on-surface-var">
              We’ll guide you through the essentials to personalise your AfriMed™ care experience.
            </p>
          </div>
        }
        footer={<Button size="lg" onClick={onContinue}>Get started</Button>}
      />
    </div>
  );
}

function hasOwnedProgress(key, userId = "") {
  const uid = String(userId || "").trim();
  if (!uid) return false;
  const stored = loadProgress(key) ?? null;
  return String(stored?.ownerUserId || "").trim() === uid;
}

function readResumeOnboardingPath(userId = "") {
  const uid = String(userId || "").trim();
  if (!uid) return "";

  const exactPath = String(readLastAuthedPath(uid) || "").trim();
  if (
    exactPath.startsWith("/onboarding/doctor") ||
    exactPath.startsWith("/onboarding/patient") ||
    exactPath.startsWith("/onboarding/pharmacy")
  ) {
    return exactPath;
  }

  const lastRole = readLastRole(uid);
  if (lastRole === "doctor" && hasOwnedProgress(DOCTOR_PROGRESS_KEY, uid)) {
    return "/onboarding/doctor?role=doctor";
  }
  if (lastRole === "patient" && hasOwnedProgress(PATIENT_PROGRESS_KEY, uid)) {
    return "/onboarding/patient?role=patient";
  }
  return "";
}

function readPersistedCategory(userId = "") {
  const uid = String(userId || "").trim();
  if (!uid) return "";

  const lastRole = readLastRole(uid);
  if (lastRole === "doctor" && hasOwnedProgress(DOCTOR_PROGRESS_KEY, uid)) {
    return "doctor";
  }
  if (lastRole === "patient" && hasOwnedProgress(PATIENT_PROGRESS_KEY, uid)) {
    return "patient";
  }
  if (PICKABLE_CATS.has(lastRole)) {
    return lastRole;
  }
  return "";
}

function logOnboardingStepEntered({ userId, step, role = "" }) {
  const uid = String(userId || "").trim();
  const stepKey = String(step || "").trim();
  if (!uid || !stepKey) return Promise.resolve();

  return writeOnboardingEvent({
    userId: uid,
    step: stepKey,
    role,
    eventType: "step_entered",
  }).catch((error) => {
    console.error("[onboarding-router] step tracking failed:", error?.message || error, {
      userId: uid,
      step: stepKey,
      role,
    });
  });
}

function getSafeFieldDescriptor(target) {
  const element = target instanceof HTMLElement ? target : null;
  if (!element) return {};
  return {
    tag: String(element.tagName || "").toLowerCase(),
    type: String(element.getAttribute("type") || "").slice(0, 32),
    name: String(element.getAttribute("name") || element.getAttribute("aria-label") || "").slice(0, 64),
  };
}

export default function OnboardingRouter() {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const user = auth?.user || null;
  const profile = auth?.profile || null;
  const loading = Boolean(auth?.loading);
  const profileResolved = auth?.profileResolved !== false;

  const [pick, setPick] = React.useState("");
  const [controlledFirstUse, setControlledFirstUse] = React.useState({
    loading: false,
    resolved: false,
    controlled: false,
    journey: "",
    role: "",
    stage: "",
    onboardingCompleted: false,
    researchContext: null,
    institutionMembership: { assigned: false, destination: "" },
  });
  const [controlledFirstUseRouteKey, setControlledFirstUseRouteKey] = React.useState("");
  const loggedCategoryRef = React.useRef("");
  const loggedFirstRenderRef = React.useRef("");
  const loggedCtaRef = React.useRef("");
  const fieldTelemetryRef = React.useRef({ focus: "", input: "" });

  // ✅ Hooks must be unconditional (no early return before this)
  const backToCategory = React.useCallback(() => {
    navigate("/onboarding?pick=1", { replace: true });
  }, [navigate]);

  const applyResearchFirstUseResult = React.useCallback((result) => {
    const nextStage = String(result?.stage || "").trim();
    const nextMembership = result?.institution_membership || { assigned: false, destination: "" };
    setControlledFirstUse((current) => ({
      ...current,
      resolved: true,
      controlled: true,
      journey: "research",
      role: "",
      stage: nextStage,
      onboardingCompleted: result?.onboarding_completed === true,
      researchContext: result?.research_context || current.researchContext,
      institutionMembership: nextMembership,
    }));
    if (nextMembership?.assigned && nextMembership?.destination) {
      navigate(nextMembership.destination, { replace: true });
      return;
    }
    navigate(`/onboarding/research?first_use=1&stage=${encodeURIComponent(nextStage)}`, { replace: true });
  }, [navigate]);

  // /onboarding/<seg?>
  const parts = pathname.replace(/^\/+/, "").split("/");
  const seg = parts[1] || null;
  const done = Boolean(profile?.onboarding_completed);
  const roleSelectionPending =
    profile?.role_selected === false && !profile?.hasDoctor && !profile?.hasPatient;
  const completedRole =
    profile?.role === "doctor" || profile?.role === "patient" || profile?.role === "pharmacy"
      ? profile.role
      : readLastRole(user?.id);
  const completedSettingsRole =
    completedRole === "doctor" || completedRole === "patient" ? completedRole : "";
  const completedSettingsWelcomePending =
    !!done &&
    !!completedSettingsRole &&
    shouldShowSettingsWelcome({
      userId: user?.id,
      role: completedSettingsRole,
      profile,
    });
  const resolvedOnboardingRole =
    normalizeCanonicalRole(profile?.role) ||
    normalizeCanonicalRole(user?.user_metadata?.primary_role) ||
    normalizeCanonicalRole(user?.user_metadata?.role);
  const manualPicker = new URLSearchParams(search).get("pick") === "1";
  const firstUseRequested = new URLSearchParams(search).get("first_use") === "1";
  const queryFirstUseStage = new URLSearchParams(search).get("stage") || "";
  const firstUseRouteKey = `${pathname || ""}${search || ""}`;
  const firstUseStage = controlledFirstUse.stage || queryFirstUseStage;
  // An authenticated user with no governed product role is intentionally
  // allowed to enter the canonical category gate even when they do not belong
  // to a server-controlled campaign. The profile's non-null storage default
  // is not a selected role; roleSelectionPending is the authority here.
  const unresolvedCategoryFirstUse =
    firstUseRequested && firstUseStage === "category" && roleSelectionPending;
  const controlledJourney =
    controlledFirstUse.journey ||
    (controlledFirstUse.role === "doctor"
      ? "clinician"
      : controlledFirstUse.role === "patient"
        ? "patient"
        : "");
  const controlledFirstUseActive =
    !done && controlledFirstUse.controlled &&
    (controlledJourney === "research" || controlledFirstUse.role === "doctor" || controlledFirstUse.role === "patient");
  const controlledFirstUseLane =
    controlledJourney === "research"
      ? "research"
      : controlledFirstUse.role === "doctor"
        ? "doctor"
        : "patient";
  const lockedCampaignCategory = controlledFirstUseActive
    ? controlledJourney === "research"
      ? "research"
      : controlledFirstUse.role
    : "";
  // A first-use transition is committed by the server before the client
  // navigates. Hold the next route briefly when its requested stage differs
  // from the last resolved value, then re-read the server-owned lifecycle.
  // The query string is never used to select a stage; it only triggers this
  // authoritative refresh and therefore cannot skip a governed step.
  const controlledStageMismatch = Boolean(
    firstUseRequested &&
      controlledFirstUseActive &&
      queryFirstUseStage &&
      controlledFirstUse.stage &&
      queryFirstUseStage !== controlledFirstUse.stage,
  );
  const awaitingControlledStageRefresh =
    controlledStageMismatch && controlledFirstUseRouteKey !== firstUseRouteKey;
  const awaitingFirstUseRouteResolution =
    firstUseRequested && !done && controlledFirstUseRouteKey !== firstUseRouteKey;
  const pharmacyEditMode =
    seg === "pharmacy" &&
    new URLSearchParams(search).get("edit") === "1";
  // MoH setup can be resumed only from an already-authorised Institution
  // workspace. The server-side setup RPC rechecks that membership; this route
  // exception never enables the disabled public MoH category tile.
  const mohResumeMode =
    seg === "moh" &&
    new URLSearchParams(search).get("resume") === "1";
  const mohAuthorised = resolvedOnboardingRole === "moh_admin";
  const mohDirectRoute = seg === "moh" && mohAuthorised;
  const previewMode =
    import.meta.env.DEV &&
    new URLSearchParams(search).get("preview") === "1";
  const resumeOnboardingPath = React.useMemo(
    () => (roleSelectionPending ? "" : readResumeOnboardingPath(user?.id)),
    [roleSelectionPending, user?.id]
  );
  const persistedCategory = React.useMemo(
    () => (roleSelectionPending ? "" : readPersistedCategory(user?.id)),
    [roleSelectionPending, user?.id]
  );
  const pharmacyResumeLocked =
    !roleSelectionPending &&
    (resolvedOnboardingRole === "pharmacy" || persistedCategory === "pharmacy");

  React.useEffect(() => {
    let alive = true;
    if (!user?.id || done) {
      setControlledFirstUse({ loading: false, resolved: true, controlled: false, journey: "", role: "", stage: "", onboardingCompleted: false, researchContext: null, institutionMembership: { assigned: false, destination: "" } });
      setControlledFirstUseRouteKey(firstUseRouteKey);
      return () => { alive = false; };
    }
    setControlledFirstUse((current) => ({ ...current, loading: true, resolved: false }));
    resolveStagingFirstUseDisposition()
      .then((result) => {
        if (!alive) return;
        setControlledFirstUse({
          loading: false,
          resolved: true,
          controlled: result.controlled === true,
          journey: String(result.journey || "").trim(),
          role: normalizeCanonicalRole(result.role),
          stage: String(result.stage || ""),
          onboardingCompleted: result.onboardingCompleted === true,
          researchContext: result.researchContext || null,
          institutionMembership: result.institutionMembership || { assigned: false, destination: "" },
        });
        setControlledFirstUseRouteKey(firstUseRouteKey);
      })
      .catch(() => {
        if (!alive) return;
        setControlledFirstUse({ loading: false, resolved: true, controlled: false, journey: "", role: "", stage: "", onboardingCompleted: false, researchContext: null, institutionMembership: { assigned: false, destination: "" } });
        setControlledFirstUseRouteKey(firstUseRouteKey);
      });
    return () => { alive = false; };
  }, [done, firstUseRouteKey, user?.id]);

  React.useEffect(() => {
    if (!profileResolved) return;
    if (roleSelectionPending) {
      clearLastRole(user?.id);
      return;
    }
    if (CATS.has(seg)) {
      if (pick !== seg) setPick(seg);
      return;
    }

    if (persistedCategory && pick !== persistedCategory) {
      setPick(persistedCategory);
    }
  }, [seg, pick, persistedCategory, profileResolved, roleSelectionPending, user?.id]);

  // Persist a resumable onboarding route and role while user is in role-specific onboarding.
  React.useEffect(() => {
    if (!profileResolved) return;
    if (!user?.id) return;
    if (seg !== "doctor" && seg !== "patient" && seg !== "pharmacy") return;
    const fullPath = `${pathname || "/"}${search || ""}${hash || ""}`;
    try {
      writeLastRole(seg, user.id);
      writeLastAuthedPath(fullPath, user.id);
    } catch {}
  }, [user?.id, seg, pathname, search, hash, profileResolved]);

  React.useEffect(() => {
    if (!profileResolved) return;
    const currentUserId = String(user?.id || "").trim();
    if (!currentUserId || CATS.has(seg)) return;
    if (loggedCategoryRef.current === currentUserId) return;
    loggedCategoryRef.current = currentUserId;
    void logOnboardingStepEntered({
      userId: currentUserId,
      step: CATEGORY_EVENT_KEY,
    });
  }, [user?.id, seg, profileResolved]);

  React.useEffect(() => {
    if (loading || !profileResolved || !user?.id) return;
    const key = `${user.id}:${seg || "category"}`;
    if (loggedFirstRenderRef.current === key) return;
    loggedFirstRenderRef.current = key;
    void logAuthAttempt({
      email: user.email || user?.user_metadata?.email || "",
      status: "success",
      stage: "onboarding_first_render",
      currentPath: `${pathname || "/"}${search || ""}${hash || ""}`,
      details: {
        userId: user.id,
        resolvedRole: resolvedOnboardingRole || "",
        selectedRoute: `${pathname || "/"}${search || ""}${hash || ""}`,
      },
    }).catch(() => {});
  }, [
    loading,
    profileResolved,
    user?.id,
    user?.email,
    user?.user_metadata?.email,
    seg,
    pathname,
    search,
    hash,
    resolvedOnboardingRole,
  ]);

  React.useEffect(() => {
    if (loading || !profileResolved || !user?.id) return undefined;
    const stepKey = seg || "category";
    const fullRoute = getPrivacySafeTelemetryRoute({ pathname });
    const timer = window.setTimeout(() => {
      const candidates = Array.from(
        document.querySelectorAll("button, a, [role='button']")
      ).filter((node) => {
        const rect = node.getBoundingClientRect?.();
        if (!rect || rect.width <= 0 || rect.height <= 0) return false;
        const style = window.getComputedStyle(node);
        return style.visibility !== "hidden" && style.display !== "none";
      });
      const primary = candidates.find((node) => {
        const text = String(node.textContent || node.getAttribute("aria-label") || "")
          .trim()
          .toLowerCase();
        return /continue|next|get started|save|done|finish|verify|start/.test(text);
      }) || candidates[0];
      if (!primary) return;
      const isDisabled =
        primary.disabled === true ||
        primary.getAttribute("aria-disabled") === "true" ||
        primary.classList?.contains("disabled");
      const key = `${user.id}:${fullRoute}:${stepKey}:${isDisabled ? "disabled" : "enabled"}`;
      if (loggedCtaRef.current === key) return;
      loggedCtaRef.current = key;
      const metadata = {
        step: stepKey,
        cta_enabled: !isDisabled,
        disabled_visible_is_not_missing: true,
      };
      void writeUserActivityEvent({
        userId: user.id,
        role: resolvedOnboardingRole || seg || "",
        route: fullRoute,
        eventType: "onboarding_cta_visible",
        metadata,
      }).catch(() => {});
      void writeUserActivityEvent({
        userId: user.id,
        role: resolvedOnboardingRole || seg || "",
        route: fullRoute,
        eventType: "onboarding_cta_enabled",
        metadata,
      }).catch(() => {});
    }, 350);
    return () => window.clearTimeout(timer);
  }, [
    loading,
    profileResolved,
    user?.id,
    pathname,
    search,
    hash,
    seg,
    resolvedOnboardingRole,
  ]);

  React.useEffect(() => {
    if (loading || !profileResolved || !user?.id) return undefined;
    const fullRoute = getPrivacySafeTelemetryRoute({ pathname });
    const stepKey = seg || "category";

    const emit = (eventType, target) => {
      const descriptor = getSafeFieldDescriptor(target);
      const key = `${eventType}:${descriptor.tag}:${descriptor.type}:${descriptor.name}`;
      if (fieldTelemetryRef.current[eventType === "onboarding_field_focus" ? "focus" : "input"] === key) {
        return;
      }
      fieldTelemetryRef.current[eventType === "onboarding_field_focus" ? "focus" : "input"] = key;
      void writeUserActivityEvent({
        userId: user.id,
        role: resolvedOnboardingRole || seg || "",
        route: fullRoute,
        eventType,
        metadata: {
          step: stepKey,
          ...descriptor,
          value_recorded: false,
        },
      }).catch(() => {});
    };

    const onFocusIn = (event) => emit("onboarding_field_focus", event.target);
    const onInput = (event) => emit("onboarding_field_input_changed", event.target);
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("input", onInput, true);
    document.addEventListener("change", onInput, true);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("input", onInput, true);
      document.removeEventListener("change", onInput, true);
    };
  }, [
    loading,
    profileResolved,
    user?.id,
    pathname,
    search,
    hash,
    seg,
    resolvedOnboardingRole,
  ]);

  React.useEffect(() => {
    if (loading || !profileResolved || !user?.id) return undefined;
    const fullRoute = getPrivacySafeTelemetryRoute({ pathname });
    let sawInput = false;
    const markInput = () => {
      sawInput = true;
    };
    document.addEventListener("pointerdown", markInput, true);
    document.addEventListener("keydown", markInput, true);
    document.addEventListener("input", markInput, true);
    const timer = window.setTimeout(() => {
      if (sawInput) return;
      void writeUserActivityEvent({
        userId: user.id,
        role: resolvedOnboardingRole || seg || "",
        route: fullRoute,
        eventType: "onboarding_user_idle_no_input",
        metadata: {
          step: seg || "category",
          idle_ms: 12000,
          classification_hint: "pause_or_no_input_not_blank_screen",
        },
      }).catch(() => {});
    }, 12000);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("pointerdown", markInput, true);
      document.removeEventListener("keydown", markInput, true);
      document.removeEventListener("input", markInput, true);
    };
  }, [
    loading,
    profileResolved,
    user?.id,
    pathname,
    search,
    hash,
    seg,
    resolvedOnboardingRole,
  ]);

  React.useEffect(() => {
    if (!user?.id) return undefined;
    if (!loading && profileResolved) return undefined;
    const timer = window.setTimeout(() => {
      void logAuthAttempt({
        email: user.email || user?.user_metadata?.email || "",
        status: "failed",
        errorMessage: "onboarding_route_hydration_timeout",
        stage: "onboarding_route_hydration_timeout",
        currentPath: `${pathname || "/"}${search || ""}${hash || ""}`,
        details: {
          userId: user.id,
          loading,
          profileResolved,
          classification_hint: "loading_or_profile_pending_not_user_pause",
        },
      }).catch(() => {});
    }, 8000);
    return () => window.clearTimeout(timer);
  }, [
    loading,
    profileResolved,
    user?.id,
    user?.email,
    user?.user_metadata?.email,
    pathname,
    search,
    hash,
  ]);

  // ✅ Auth gate
  // A server-governed first-use route must wait for its disposition before
  // evaluating the unavailable guard. The resolver starts in an effect, so
  // checking only `loading` here races the first render and can reject a
  // valid confirmed session before the server response arrives.
  if (
    loading ||
    !profileResolved ||
    (user && !done && !controlledFirstUse.resolved) ||
    awaitingControlledStageRefresh ||
    awaitingFirstUseRouteResolution
  ) {
    return <RouteFallback label="Loading AfriMed..." />;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (
    firstUseRequested &&
    !done &&
    !controlledFirstUse.controlled &&
    !unresolvedCategoryFirstUse
  ) {
    return <Navigate to="/login?first_use=unavailable" replace />;
  }
  if (
    controlledJourney === "research" &&
    controlledFirstUse.onboardingCompleted &&
    controlledFirstUse.institutionMembership?.assigned &&
    controlledFirstUse.institutionMembership?.destination
  ) {
    return <Navigate to={controlledFirstUse.institutionMembership.destination} replace />;
  }
  if (
    controlledJourney === "research" &&
    controlledFirstUse.onboardingCompleted &&
    hasPasswordConfigured(user)
  ) {
    return <Navigate to="/settings/research" replace />;
  }

  // The server re-resolution above has completed for this URL. If its
  // requested stage was stale, replace it with the server-owned route rather
  // than leaving the user on a loading surface or trusting the query string.
  if (controlledStageMismatch) {
    return (
      <Navigate
        to={`/onboarding/${controlledFirstUseLane}?first_use=1&stage=${encodeURIComponent(firstUseStage || (controlledJourney === "research" ? "research_basic_details" : "cookies"))}`}
        replace
      />
    );
  }

  // ✅ Onboarding-once gate
  if (done && !previewMode && !pharmacyEditMode && !mohResumeMode && !mohDirectRoute) {
    return (
      <Navigate
        to={
          completedSettingsWelcomePending
            ? `/settings/${completedSettingsRole}`
            : completedRole === "doctor"
            ? "/home/doctor"
            : completedRole === "patient"
              ? "/home/patient"
              : completedRole === "pharmacy"
                ? "/pharmacy/dashboard"
                : "/settings"
        }
        replace
      />
    );
  }

  // The server-owned stage also selects the lane route needed by the existing
  // password surface. This is navigation only: the resolver still rejects a
  // manually supplied role or an invalid transition.
  if (controlledFirstUseActive && !firstUseRequested) {
    return (
      <Navigate
        to={`/onboarding/${controlledFirstUseLane}?first_use=1&stage=${encodeURIComponent(firstUseStage || (controlledJourney === "research" ? "research_basic_details" : "cookies"))}`}
        replace
      />
    );
  }

  if (
    firstUseRequested &&
    controlledFirstUseActive &&
    !CATS.has(seg) &&
    firstUseStage !== "category"
  ) {
    return (
      <Navigate
        to={`/onboarding/${controlledFirstUseLane}?first_use=1&stage=${encodeURIComponent(firstUseStage || (controlledJourney === "research" ? "research_basic_details" : "cookies"))}`}
        replace
      />
    );
  }

  if (
    firstUseRequested &&
    controlledFirstUseActive &&
    CATS.has(seg) &&
    firstUseStage === "category"
  ) {
    return <Navigate to="/onboarding?first_use=1&stage=category" replace />;
  }

  // Picker (always at /onboarding)
  if (!CATS.has(seg)) {
    if (lockedCampaignCategory) {
      if (firstUseStage !== "category") {
        return <RouteFallback label="Preparing your AfriMed™ setup..." />;
      }
      return (
        <StepCategory
          step={1}
          total={1}
          value={lockedCampaignCategory}
          lockedRole={lockedCampaignCategory}
          onChange={() => {}}
          onBack={() => navigate("/login", { replace: true })}
          onContinue={(selectedCategory) => {
            if (selectedCategory !== lockedCampaignCategory) return;
            const nextStage = selectedCategory === "research" ? "research_basic_details" : "password";
            const nextLane = selectedCategory === "research" ? "research" : selectedCategory;
            advanceStagingFirstUse(nextStage)
              .then(() => navigate(`/onboarding/${nextLane}?first_use=1&stage=${nextStage}`, { replace: true }))
              .catch(() => {});
          }}
        />
      );
    }
    if (!previewMode && pharmacyResumeLocked) {
      return <Navigate to="/onboarding/pharmacy" replace />;
    }
    if (
      !previewMode &&
      !manualPicker &&
      (resolvedOnboardingRole === "doctor" || resolvedOnboardingRole === "patient" || resolvedOnboardingRole === "pharmacy")
    ) {
      if (resolvedOnboardingRole === "pharmacy") {
        return <Navigate to="/onboarding/pharmacy" replace />;
      }
      return (
        <Navigate
          to={`/onboarding/${resolvedOnboardingRole}?role=${resolvedOnboardingRole}`}
          replace
        />
      );
    }
    if (resumeOnboardingPath && !previewMode && !manualPicker) {
      return <Navigate to={resumeOnboardingPath} replace />;
    }
    return (
      <StepCategory
        step={1}
        total={1}
        value={pick}
        onChange={(k) => {
          if (!CATS.has(k)) return;
          setPick(k);
        }}
        onBack={() => navigate("/login", { replace: true })}
        onContinue={(selectedCategory) => {
          const nextCategory = CATS.has(selectedCategory) ? selectedCategory : pick;
          if (!CATS.has(nextCategory)) return;
          if (firstUseRequested && unresolvedCategoryFirstUse) {
            selectSelfServiceFirstUseCategory(nextCategory)
              .then((result) => {
                const nextRole = normalizeCanonicalRole(result?.role);
                const nextStage = String(result?.stage || "password").trim() || "password";
                if (!nextRole) return;
                setControlledFirstUse((current) => ({
                  ...current,
                  loading: false,
                  resolved: true,
                  controlled: true,
                  journey: nextRole === "doctor" ? "clinician" : "patient",
                  role: nextRole,
                  stage: nextStage,
                  onboardingCompleted: false,
                }));
                navigate(`/onboarding/${nextRole}?first_use=1&stage=${encodeURIComponent(nextStage)}`, {
                  replace: true,
                });
              })
              .catch(() => {});
            return;
          }
          navigate(`/onboarding/${nextCategory}${search || ""}`, { replace: true });
        }}
      />
    );
  }

  switch (seg) {
    case "doctor":
      if (firstUseRequested && controlledFirstUseActive) {
        return (
          <ControlledDoctorFirstUse
            stage={firstUseStage}
            backToCategory={backToCategory}
          />
        );
      }
      return <DoctorOnboarding backToCategory={backToCategory} />;
        case "patient":
          if (firstUseRequested && firstUseStage === "cookies") {
            return <FirstUseCookieGate />;
      }
      if (firstUseRequested && firstUseStage === "password") {
        return (
          <ChangePassword
            intent="create"
            firstUseNextStage="details"
            firstUseReturnTo="/onboarding/patient?first_use=1&stage=details"
          />
        );
      }
      if (firstUseRequested && firstUseStage === "category") {
        return <Navigate to="/onboarding?first_use=1&stage=category" replace />;
      }
      if (firstUseRequested && firstUseStage === "get_started") {
        return (
          <PatientFirstUseStart
            onBack={() => navigate("/login", { replace: true })}
            onContinue={() => {
              advanceStagingFirstUse("cookies")
                .then(() => {
                  navigate("/onboarding/patient?first_use=1&stage=cookies", { replace: true });
                  window.setTimeout(() => window.afrimedCookies?.open?.(), 0);
                })
                .catch(() => {});
            }}
          />
        );
      }
      return (
        <PatientOnboarding
          backToCategory={backToCategory}
          preserveFirstUseOrder={
            firstUseRequested && firstUseStage === "details" && controlledFirstUseActive
          }
        />
      );
    case "research":
      if (!firstUseRequested || !controlledFirstUseActive || controlledJourney !== "research") {
        return <Navigate to="/settings" replace />;
      }
      return (
        <ResearchPublicHealthOnboarding
          stage={firstUseStage}
          context={controlledFirstUse.researchContext}
          membership={controlledFirstUse.institutionMembership}
          onSaved={applyResearchFirstUseResult}
          onBack={() => {
            if (firstUseStage === "research_basic_details") {
              navigate("/login", { replace: true });
              return Promise.resolve();
            }
            return moveResearchFirstUseBack().then(applyResearchFirstUseResult);
          }}
          onEditProfile={() => navigate("/settings/research?first_use_review=1")}
          onContinueToAccount={() => navigate("/settings/research/account?first_use=1&stage=password")}
        />
      );
    case "employer":
      return <EmployerOnboarding backToCategory={backToCategory} />;
    case "moh":
      if (!mohAuthorised) return <Navigate to="/settings" replace />;
      return <MoHOnboarding backToCategory={backToCategory} resumeMode={mohResumeMode} />;
    case "pharmacy":
      return <PharmacyOnboarding backToCategory={backToCategory} />;
    default:
      return <Navigate to={`/onboarding${search || ""}`} replace />;
  }
}
